<template>
  <!-- Add Holiday -->
  <div class="modal fade dq-holiday-modal" id="new_holiday" tabindex="-1">
    <div
      class="modal-dialog modal-dialog-centered modal-md modal-dialog-scrollable dq-holiday-dialog"
    >
      <div class="modal-content dq-modal-content" :key="addKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-sky">
              <i class="ti ti-calendar-event"></i>
            </div>
            <div class="dq-headtext">
              <h4 class="modal-title dq-title mb-0">Add Holiday</h4>
              <div class="dq-subtitle">
                Create a holiday entry for scheduling, attendance, and payroll
                visibility.
              </div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitCreate">
          <div class="modal-body dq-modal-body">
            <div class="dq-card">
              <div class="dq-card-head">
                <div class="dq-card-title">
                  <i class="ti ti-pencil"></i> Holiday Details
                </div>
                <div class="dq-card-sub">
                  Give it a clear title, date, and an optional note.
                </div>
              </div>

              <div class="row g-3 mt-0">
                <div class="col-12">
                  <label class="form-label dq-label">Holiday Title</label>
                  <input
                    v-model="form.title"
                    class="form-control dq-input"
                    placeholder="e.g. New Year's Day"
                  />
                  <div class="dq-help">
                    This is what staff will see in calendars and reports.
                  </div>
                </div>

                <div class="col-12">
                  <label class="form-label dq-label">Date</label>
                  <div class="dq-datewrap">
                    <date-picker
                      v-model="form.holiday_date"
                      class="form-control datetimepicker dq-input dq-date"
                      placeholder="dd/mm/yyyy"
                      :editable="true"
                      :clearable="false"
                      :input-format="dateFormat"
                    />
                    <span class="dq-date-ico"
                      ><i class="ti ti-calendar"></i
                    ></span>
                  </div>
                  <div class="dq-help">
                    Pick the exact holiday date (stored as YYYY-MM-DD).
                  </div>
                </div>

                <div class="col-12">
                  <label class="form-label dq-label">Description</label>
                  <textarea
                    v-model="form.description"
                    class="form-control dq-input"
                    rows="3"
                    placeholder="Optional: short note about the holiday..."
                  ></textarea>
                </div>

                <div class="col-12">
                  <label class="form-label dq-label">Status</label>
                  <vue-select
                    v-model="form.status"
                    :options="['Active', 'Inactive']"
                    placeholder="Select Status"
                    class="dq-vselect"
                    :clearable="false"
                  />
                  <div
                    class="dq-status mt-2"
                    :class="
                      form.status === 'Active'
                        ? 'dq-status-ok'
                        : 'dq-status-warn'
                    "
                  >
                    <i
                      class="ti"
                      :class="
                        form.status === 'Active' ? 'ti-check' : 'ti-clock'
                      "
                    ></i>
                    <span>{{
                      form.status === "Active"
                        ? "Visible and in use"
                        : "Hidden / not applied"
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-white border dq-btn dq-btn-muted me-2"
              data-bs-dismiss="modal"
              :disabled="addingHoliday"
            >
              Cancel
            </button>

            <button
              type="submit"
              class="btn btn-primary dq-btn dq-btn-primary"
              :disabled="addingHoliday"
            >
              <span
                v-if="addingHoliday"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Add Holiday <i class="ti ti-arrow-right ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Edit Holiday -->
  <div class="modal fade dq-holiday-modal" id="edit_holiday" tabindex="-1">
    <div
      class="modal-dialog modal-dialog-centered modal-md modal-dialog-scrollable dq-holiday-dialog"
    >
      <div class="modal-content dq-modal-content" :key="editKey">
        <!-- Header -->
        <div class="modal-header dq-modal-header">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-amber">
              <i class="ti ti-edit"></i>
            </div>
            <div class="dq-headtext">
              <h4 class="modal-title dq-title mb-0">Edit Holiday</h4>
              <div class="dq-subtitle">Update the title, date, or status.</div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <form @submit.prevent="submitEdit">
          <div class="modal-body dq-modal-body">
            <div class="dq-card">
              <div class="dq-card-head">
                <div class="dq-card-title">
                  <i class="ti ti-settings"></i> Holiday Settings
                </div>
                <div class="dq-card-sub">
                  Make adjustments and save changes.
                </div>
              </div>

              <div class="row g-3 mt-0">
                <div class="col-12">
                  <label class="form-label dq-label">Holiday Title</label>
                  <input
                    v-model="form.title"
                    class="form-control dq-input"
                    placeholder="e.g. New Year's Day"
                  />
                </div>

                <div class="col-12">
                  <label class="form-label dq-label">Date</label>
                  <div class="dq-datewrap">
                    <date-picker
                      v-model="form.holiday_date"
                      class="form-control datetimepicker dq-input dq-date"
                      placeholder="dd/mm/yyyy"
                      :editable="true"
                      :clearable="false"
                      :input-format="dateFormat"
                    />
                    <span class="dq-date-ico"
                      ><i class="ti ti-calendar"></i
                    ></span>
                  </div>
                </div>

                <div class="col-12">
                  <label class="form-label dq-label">Description</label>
                  <textarea
                    v-model="form.description"
                    class="form-control dq-input"
                    rows="3"
                    placeholder="Optional: short note about the holiday..."
                  ></textarea>
                </div>

                <div class="col-12">
                  <label class="form-label dq-label">Status</label>
                  <select v-model="form.status" class="form-select dq-input">
                    <option>Active</option>
                    <option>Inactive</option>
                  </select>

                  <div
                    class="dq-status mt-2"
                    :class="
                      form.status === 'Active'
                        ? 'dq-status-ok'
                        : 'dq-status-warn'
                    "
                  >
                    <i
                      class="ti"
                      :class="
                        form.status === 'Active' ? 'ti-check' : 'ti-clock'
                      "
                    ></i>
                    <span>{{
                      form.status === "Active"
                        ? "Visible and in use"
                        : "Hidden / not applied"
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="modal-footer dq-modal-footer">
            <button
              type="button"
              class="btn btn-white border dq-btn dq-btn-muted me-2"
              data-bs-dismiss="modal"
              :disabled="addingHoliday"
            >
              Cancel
            </button>

            <button
              type="submit"
              class="btn btn-primary dq-btn dq-btn-primary"
              :disabled="!isEdit || addingHoliday"
            >
              <span
                v-if="addingHoliday"
                class="spinner-border spinner-border-sm me-2"
              ></span>
              Save Changes <i class="ti ti-device-floppy ms-2"></i>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Delete Modal -->
  <div class="modal fade dq-holiday-modal" id="delete_modal" tabindex="-1">
    <div class="modal-dialog modal-dialog-centered modal-sm">
      <div class="modal-content dq-modal-content">
        <div class="modal-header dq-modal-header dq-danger-head">
          <div class="dq-titlewrap">
            <div class="dq-hero dq-hero-red">
              <i class="ti ti-trash"></i>
            </div>
            <div>
              <h5 class="modal-title dq-title mb-0">Confirm Delete</h5>
              <div class="dq-subtitle">This action cannot be undone.</div>
            </div>
          </div>

          <button
            type="button"
            class="btn-close custom-btn-close dq-close"
            data-bs-dismiss="modal"
            aria-label="Close"
          >
            <i class="ti ti-x"></i>
          </button>
        </div>

        <div class="modal-body dq-modal-body">
          <div class="dq-danger">
            <i class="ti ti-alert-octagon"></i>
            <div>
              <div class="dq-danger-title">Delete this holiday?</div>
              <div class="dq-danger-sub">
                It will be removed from holiday lists and related views.
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer dq-modal-footer">
          <button
            type="button"
            class="btn btn-light dq-btn dq-btn-muted"
            data-bs-dismiss="modal"
            :disabled="deleting"
          >
            Cancel
          </button>

          <button
            class="btn btn-danger dq-btn dq-btn-danger"
            :disabled="deleting"
            @click="confirmDelete"
          >
            <span
              v-if="deleting"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            Delete <i class="ti ti-trash ms-2"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from "vue";
import { useHolidayStore } from "@/stores/holidayStore";
import { toast } from "@/utils/toast";

const props = defineProps({
  editItem: { type: Object, default: null },
});
const emit = defineEmits(["saved"]);

const holidayStore = useHolidayStore();

const deleting = ref(false);
const addingHoliday = computed(() => holidayStore.loading);
const dateFormat = "dd/MM/yyyy";

// force-refresh keys (helps reset stubborn inputs between opens)
const addKey = ref(0);
const editKey = ref(0);

const form = ref(blankForm());

function blankForm() {
  return {
    title: "",
    holiday_date: null, // keep Date in UI; convert before submit
    description: "",
    status: "Active",
  };
}

// mutate in-place so v-model inputs update reliably
function resetForm() {
  const blank = blankForm();
  for (const k in blank) form.value[k] = blank[k];
}

// keep the form in sync with editItem
watch(
  () => props.editItem,
  (val) => {
    if (val) {
      form.value.title = val.title ?? "";
      form.value.holiday_date = val.holiday_date
        ? new Date(val.holiday_date)
        : null;
      form.value.description = val.description ?? "";
      form.value.status = val.status ?? "Active";
      editKey.value++; // refresh edit modal internals
    } else {
      resetForm();
    }
  },
  { immediate: true }
);

const isEdit = computed(() => !!props.editItem?.id);

// --- DATE UTILS: send YYYY-MM-DD to avoid UTC shifting on backend
function toYMD(d) {
  if (!d) return null;
  if (typeof d === "string") return d; // already a string
  try {
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const day = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  } catch {
    return null;
  }
}

async function submitCreate() {
  if (!form.value.title || !form.value.holiday_date) {
    toast("Title and Holiday Date are required", "error");
    return;
  }
  const payload = {
    ...form.value,
    holiday_date: toYMD(form.value.holiday_date),
  };
  await holidayStore.create(payload);
  closeNew();
  resetForm();
  addKey.value++; // refresh add modal UI
  emit("saved");
}

async function submitEdit() {
  if (!props.editItem?.id) return;
  const payload = {
    ...form.value,
    holiday_date: toYMD(form.value.holiday_date),
  };
  await holidayStore.update(props.editItem.id, payload);
  closeEdit();
  resetForm();
  editKey.value++; // refresh edit modal UI
  emit("saved");
}

function closeNew() {
  document.querySelector('#new_holiday [data-bs-dismiss="modal"]')?.click();
}
function closeEdit() {
  document.querySelector('#edit_holiday [data-bs-dismiss="modal"]')?.click();
}

async function confirmDelete() {
  deleting.value = true;
  try {
    if (!props.editItem?.id) return;
    await holidayStore.remove(props.editItem.id);
    emit("saved");
  } catch (e) {
    console.error(e);
  } finally {
    deleting.value = false;
  }
}

// ensure state clears whenever modals hide/show
let cleanup = null;
onMounted(() => {
  const addEl = document.getElementById("new_holiday");
  const editEl = document.getElementById("edit_holiday");

  const handleAddShow = () => {
    resetForm();
    addKey.value++;
  };
  const handleAddHidden = () => {
    resetForm();
  };
  const handleEditHidden = () => {
    resetForm();
    editKey.value++;
  };

  addEl?.addEventListener("show.bs.modal", handleAddShow);
  addEl?.addEventListener("hidden.bs.modal", handleAddHidden);
  editEl?.addEventListener("hidden.bs.modal", handleEditHidden);

  cleanup = () => {
    addEl?.removeEventListener("show.bs.modal", handleAddShow);
    addEl?.removeEventListener("hidden.bs.modal", handleAddHidden);
    editEl?.removeEventListener("hidden.bs.modal", handleEditHidden);
  };
});

onBeforeUnmount(() => cleanup && cleanup());
</script>

<style scoped>
/* ===== Shell ===== */
.dq-holiday-dialog {
  max-width: 560px;
}
.dq-modal-content {
  border-radius: 22px;
  border: none;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08),
    0 40px 90px rgba(15, 23, 42, 0.14);
}

.dq-modal-header {
  padding: 18px 20px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  background: linear-gradient(180deg, rgba(56, 189, 248, 0.16), #fff);
}
.dq-danger-head {
  background: linear-gradient(180deg, rgba(239, 68, 68, 0.12), #fff);
}

.dq-modal-body {
  padding: 16px 20px;
}

/* Reliable scroll + sticky footer */
.modal-dialog.modal-dialog-scrollable .modal-content {
  max-height: 92vh;
  display: flex;
  flex-direction: column;
}
.modal-dialog.modal-dialog-scrollable .modal-body {
  overflow-y: auto;
  max-height: calc(92vh - 140px);
  overscroll-behavior: contain;
}
.dq-modal-footer {
  padding: 14px 20px;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  position: sticky;
  bottom: 0;
  z-index: 2;
}

/* Header */
.dq-titlewrap {
  display: flex;
  align-items: center;
  gap: 12px;
}
.dq-title {
  font-weight: 950;
  letter-spacing: -0.35px;
}
.dq-subtitle {
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}
.dq-close {
  opacity: 0.9;
}
.dq-close:hover {
  opacity: 1;
}

/* Hero icons */
.dq-hero {
  width: 46px;
  height: 46px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}
.dq-hero-sky {
  background: rgba(56, 189, 248, 0.18);
  color: rgb(2, 132, 199);
}
.dq-hero-amber {
  background: rgba(245, 158, 11, 0.16);
  color: rgb(217, 119, 6);
}
.dq-hero-red {
  background: rgba(239, 68, 68, 0.14);
  color: rgb(220, 38, 38);
}

/* Cards */
.dq-card {
  border-radius: 18px;
  border: 1px solid rgba(15, 23, 42, 0.06);
  background: #fff;
  padding: 14px;
}
.dq-card-head {
  padding: 2px 2px 10px;
}
.dq-card-title {
  font-weight: 950;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.dq-card-sub {
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.6);
}

/* Inputs */
.dq-label {
  font-size: 0.82rem;
  font-weight: 900;
}
.dq-input {
  border-radius: 14px;
  padding: 10px 12px;
}
.dq-input:focus {
  border-color: rgba(56, 189, 248, 0.55);
  box-shadow: 0 0 0 3px rgba(56, 189, 248, 0.14);
}
.dq-help {
  margin-top: 6px;
  font-size: 0.8rem;
  color: rgba(15, 23, 42, 0.55);
}

/* DatePicker wrapper (icon + alignment) */
.dq-datewrap {
  position: relative;
}
.dq-date {
  padding-right: 40px;
}
.dq-date-ico {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(15, 23, 42, 0.55);
  pointer-events: none;
}

/* Status pill */
.dq-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 14px;
  font-size: 0.82rem;
  font-weight: 900;
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-status-ok {
  background: rgba(16, 185, 129, 0.12);
  color: rgb(5, 150, 105);
}
.dq-status-warn {
  background: rgba(245, 158, 11, 0.12);
  color: rgb(217, 119, 6);
}

/* Buttons */
.dq-btn {
  border-radius: 12px;
  padding: 10px 16px;
  font-weight: 900;
}
.dq-btn-muted {
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
}
.dq-btn-primary {
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.1);
}
.dq-btn-danger {
  box-shadow: 0 12px 24px rgba(239, 68, 68, 0.12);
}

/* Delete warning */
.dq-danger {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px;
  border-radius: 18px;
  border: 1px solid rgba(239, 68, 68, 0.14);
  background: rgba(239, 68, 68, 0.08);
}
.dq-danger i {
  font-size: 18px;
  color: rgb(220, 38, 38);
  margin-top: 2px;
}
.dq-danger-title {
  font-weight: 950;
}
.dq-danger-sub {
  margin-top: 6px;
  font-size: 0.82rem;
  color: rgba(15, 23, 42, 0.7);
}
</style>
f
